import { Button, Card, css, Heading, Paragraph, Plus1Icon, styled, textEllipsis } from 'canoe';
import { connectWithHammock } from 'hammock';
import isEqual from 'lodash/isEqual';
import moment from 'moment';
import React, { MouseEvent, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { compose } from 'redux';
import { ENDPOINTS } from 'site-constants';

import { Snapshot } from '@candu-ai/snapshots/types';
import {
  setPreviewSelector,
  setPreviewSelectorLocation,
  setSnapshot,
  useCurrentSnapshot,
} from 'craft/state';
import { usePlacements } from 'craft/state/placements';

import { SnapshotThumbnail } from './snapshotThumbnail';

export const ListCard = styled(Card)<{
  active?: boolean;
  noDetails?: boolean;
  interactive?: boolean;
  withCTA?: boolean;
}>`
  ${({ theme, active, noDetails, interactive = true, withCTA = true }) => css`
    height: 235px;
    position: relative;
    border-radius: ${theme.borderRadius.small};
      //background-color: ${theme.colors.OnyxGrayL10};
    border: 1px solid ${theme.colors.OnyxGray};
    margin: ${theme.spaces.xs};
    padding: ${theme.spaces.s};

    &:first-child {
      margin-top: ${theme.spaces.l};
    }

    ${
      noDetails &&
      css`
        height: 215px;
      `
    }
    ${
      active &&
      css`
        ${
          withCTA &&
          css`
            height: 260px;
          `
        }
        border-color: ${theme.colors.AquaTeal};
      `
    }

    ${
      interactive
        ? css`
            &:hover {
              border: 1px solid ${theme.colors.AquaTeal};
            }
          `
        : css`
            height: auto;
            padding: ${theme.spaces.normal} ${theme.spaces.s};
          `
    }
  `}
`;

const EmptyListHeading = styled(Heading)`
  margin-top: 0;
  line-height: 1.1;
`;

const EmptyListText = styled(Paragraph)`
  line-height: 1.2;
`;

export const SelectorText = styled.div`
  ${({ theme }) => css`
    position: absolute;
    top: -1px;
    left: -1px;
    background-color: ${theme.colors.AquaTeal};
    color: ${theme.colors.White};
    font-size: ${theme.fonts.small.fontSize};
    padding: ${theme.spaces.xxs} ${theme.spaces.xs};
    border-radius: ${theme.borderRadius.small} 0 ${theme.borderRadius.small} 0;
    max-width: 200px;
    ${textEllipsis};
  `}
`;

export const SnapshotDetails = styled.div<{ active: boolean }>`
  ${({ theme, active }) => css`
    margin-top: ${theme.spaces.xs};

    ${active &&
    css`
      max-width: 190px;
    `}
    span {
      display: block;
      font-size: 14px;
      color: ${theme.colors.White};
      ${textEllipsis};

      &.small {
        font-size: ${theme.fonts.small.fontSize};
      }
    }
  `}
`;

const EditButtonWrapper = styled.div`
  ${({ theme }) => css`
    position: absolute;
    bottom: ${theme.spaces.s};
    right: ${theme.spaces.s};
  `}
`;

const CTA = styled.div`
  ${({ theme }) => css`
    padding: 0 ${theme.spaces.xs};
  `}
`;

interface PlacementsListProps {
  contentId: string;
  setEditingPlacement: any;
  snapshots: Snapshot[];
  onAddSnapshot: VoidFunction;
  onAddSnapshotPlacement: VoidFunction;
}

const PlacementsListImpl = ({
  setEditingPlacement,
  contentId,
  snapshots,
  onAddSnapshot,
  onAddSnapshotPlacement,
}: PlacementsListProps) => {
  const dispatch = useDispatch();
  const { placements } = usePlacements();
  const { placement: activeSnapshotPlacement } = useCurrentSnapshot();

  const [searchParams, setSearchParams] = useSearchParams();
  // when we want to edit a specific snapshot placement on load, for example when landing in the editor after retaking a snapshot
  const triggerSnapshot = snapshots?.find(
    (s) => s.id === searchParams.get('editSnapshotPlacementId'),
  );

  const noPlacements = !placements || placements.length === 0;
  const noSnapshots = !snapshots || snapshots.length === 0;

  const defaultPlacement = !triggerSnapshot ? placements?.[0] : undefined;

  const [activeItem, setActiveItem] = useState(activeSnapshotPlacement || defaultPlacement);

  useEffect(() => {
    // always clear preview selector values when loading the list, in case a placement was being edited
    // safer than depending on the goBack function to implement this
    dispatch(setPreviewSelector(undefined));
    dispatch(setPreviewSelectorLocation(undefined));
  }, []);

  useEffect(() => {
    // if we have a snapshot but no placements OR we are loading a specific snapshot that doesn't have a placement yet
    // add a new placement for active snapshot
    if ((triggerSnapshot && !activeSnapshotPlacement) || (noPlacements && !noSnapshots)) {
      onAddSnapshotPlacement();
    }

    // if we are loading a specific snapshot that has a placement, trigger placement edit
    if (triggerSnapshot && activeSnapshotPlacement) {
      setEditingPlacement(activeSnapshotPlacement);
    }

    if (triggerSnapshot) {
      // once we are loaded in the correct state, remove search param
      if (searchParams.has('editSnapshotPlacementId')) {
        searchParams.delete('editSnapshotPlacementId');
        setSearchParams(searchParams);
      }
    }
  }, [noPlacements, noSnapshots, triggerSnapshot, activeSnapshotPlacement]);

  useEffect(() => {
    // if we're in a state where the wrong placement is active in the list, set the correct placement as active
    // this can happen due to a delayed refresh of the placements endpoint and the initial
    // state doesn't get the correct value
    const placementAvailableForSelection = activeSnapshotPlacement || defaultPlacement;
    if (placementAvailableForSelection && !isEqual(activeItem, placementAvailableForSelection)) {
      // avoid setting a value if there are no available placements or we have the correct placement selected
      setActiveItem(placementAvailableForSelection);
    }
  }, [placements]);

  useEffect(() => {
    // if the selected placement has a snapshot, but it hasn't applied to the active snapshot placement
    // this might happen when auto-selecting the active item (for example after a snapshot from the list is deleted)
    if (activeItem?.snapshotId && !activeSnapshotPlacement) {
      const snapshot = snapshots.find((s) => s.id === activeItem.snapshotId);
      if (snapshot) {
        dispatch(setSnapshot(snapshot, activeItem));
      }
    }
  }, [activeItem, activeSnapshotPlacement, snapshots]);

  if (noPlacements && noSnapshots) {
    return (
      <ListCard interactive={false}>
        <EmptyListHeading size="XS" color="White">
          You haven’t placed this content anywhere.
        </EmptyListHeading>
        <EmptyListText size="short" color="White">
          Use Snapshots to seamlessly preview this content within your product.
        </EmptyListText>
        <Button variant="secondary" onClick={() => onAddSnapshot()} fullWidth>
          Add Snapshot
        </Button>
      </ListCard>
    );
  }

  return (
    <div style={{ color: 'white' }}>
      {(placements || []).map((placement, idx) => {
        const snapshot = snapshots.find((s) => s.id === placement.snapshotId);

        const setPlacementActive = (e: MouseEvent) => {
          e.preventDefault();
          if (snapshot) {
            dispatch(setSnapshot(snapshot, placement));
          } else {
            dispatch(setSnapshot(undefined, undefined));
          }

          setActiveItem(placement);
        };

        const active = activeItem ? activeItem.id === placement.id : idx === 0;

        return (
          <ListCard
            key={placement.id}
            active={active}
            onClick={!active ? setPlacementActive : undefined}
            noDetails={!snapshot}
          >
            {placement.selector && <SelectorText>{placement.selector}</SelectorText>}
            <SnapshotThumbnail contentId={contentId} snapshotId={placement.snapshotId} />

            {!!snapshot && (
              <SnapshotDetails active={active}>
                <span>{snapshot.pageUrl}</span>
                <span className="small">{moment(snapshot.createdAt).format('DD MMM YYYY')}</span>
              </SnapshotDetails>
            )}

            {active && (
              <EditButtonWrapper>
                <Button
                  variant="white"
                  appearance="text"
                  inline
                  onClick={() => setEditingPlacement(placement)}
                >
                  Edit
                </Button>
              </EditButtonWrapper>
            )}
          </ListCard>
        );
      })}
      <CTA>
        <Button
          variant="secondary"
          appearance="outline"
          icon={Plus1Icon}
          fullWidth
          mt="s"
          onClick={() => onAddSnapshot()}
        >
          Add new Snapshot
        </Button>
      </CTA>
    </div>
  );
};

const withSnapshots = connectWithHammock(({ contentId }) => ({
  resourceName: 'snapshots',
  endpoint: ENDPOINTS.snapshots,
  query: { finder: 'byContentId', id: contentId },
}));

export const PlacementsList = compose(withSnapshots)(PlacementsListImpl);
